<template>
  <tr>

    <td>
      <b-button
        :disabled="item.ape_id !== ''"
        class="ml-auto btn-compact"
        size="sm"
        variant="outline-danger"
        pill
        @click="$emit('remove', index)"
      >
        Remove
        <i class="fa fa-minus" aria-hidden="true"></i>
      </b-button>
      <b-button
        v-if="isAdminRemovable && item.ape_id"
        class="ml-2 btn-compact"
        size="sm"
        variant="outline-danger"
        pill
        @click="$emit('remove-admin', item)"
      >
        Remove Admin
        <i class="fa fa-minus" aria-hidden="true"></i>
      </b-button>
    </td>
    <th scope="row">{{ index + 1 }}</th>
    <td>
      <model-list-select
        :isDisabled="item.ape_id !== ''"
        :list="ledgerData"
        v-model="item.addTask_ledgerId"
        option-value="ledgerId"
        option-text="ledger"
        placeholder="Select "
      />
      {{ item.ape_transaction_no || '' }}
      <span class="error" v-if="item.addTask_ledgerId_err">{{ item.addTask_ledgerId_err }}</span>
    </td>
    <td>
      <b-form-input
        :disabled="item.ape_id !== ''"
        :formatter="capitalizeAll"
        v-model="item.addTask_amount"
        type="number"
        step="0.01"
        inputmode="decimal"
        placeholder="Enter amount"
        @wheel="handleWheel"
        @input="onNumberGuard"
        @change="onAmountChange"
        @blur="validateAmount"
      />
      <span class="error" v-if="item.addTask_amount_err">{{ item.addTask_amount_err }}</span>
    </td>
    <td>
      <model-list-select
        :isDisabled="item.ape_id !== ''"
        :list="currencies"
        v-model="item.addTask_currency_id"
        option-value="currency_id"
        option-text="currencyCode"
        placeholder="Currency"
      />
      <span class="error" v-if="item.addTask_currency_id_err">{{ item.addTask_currency_id_err }}</span>
    </td>
    <td>
      <b-form-radio-group
        v-model="item.addTask_scCategory"
        :options="scCategoryList"
        class="mb-1"
        value-field="SCVal"
        text-field="SCText"
        buttons
        size="sm"
        button-variant="outline-primary"
        :disabled="item.ape_id !== ''"
        @input="onSCCategoryChange"
      />
      <span class="error" v-if="(!item.addTask_scCategory) && item.addTask_scCategory_err">{{ item.addTask_scCategory_err }}</span>
    </td>
    <td>
      <b-form-input
        v-if="item.addTask_scCategory === 'Yes'"
        :formatter="capitalizeAll"
        type="number"
        step="0.01"
        inputmode="decimal"
        v-model="item.addTask_selling_amt"
        @wheel="handleWheel"
        @input="onNumberGuard"
        @change="onSellingAmountChange"
        @blur="validateSellingAmount"
        :disabled="item.ape_id !== ''"
        placeholder="Selling amount"
      />
      <span class="error" v-if="item.addTask_selling_amt_err">{{ item.addTask_selling_amt_err }}</span>
      <span v-else-if="item.addTask_scCategory === 'No'">No</span>
    </td>
    <td>
      <b-badge 
        v-if="item.approval_status"
        :variant="getApprovalStatusVariant(item.approval_status)"
        style="font-size: 0.75rem; padding: 4px 8px;"
      >
        {{ item.approval_status }}
      </b-badge>
      <span v-else class="text-muted">-</span>
    </td>
    <td>
      <b-form-textarea
        :disabled="item.ape_id !== ''"
        :formatter="capitalizeAll"
        v-model="item.addTask_remark"
        rows="1"
        max-rows="3"
        placeholder="Remarks"
      />
      <span class="error" v-if="item.addTask_remark_err">{{ item.addTask_remark_err }}</span>
    </td>
  </tr>
</template>

<script>
import { ModelListSelect } from 'vue-search-select'

export default {
  name: 'AddTaskRow',
  components: { ModelListSelect },
  props: {
    item: { type: Object, required: true },
    index: { type: Number, required: true },
    ledgerData: { type: Array, required: true },
    scCategoryList: { type: Array, required: true },
    isAdminRemovable: { type: Boolean, default: false },
    currencies: { type: Array, required: false, default: () => [] },
  },
  methods: {
    capitalizeAll(v) {
      return v
    },
    getApprovalStatusVariant(status) {
      if (!status) return 'secondary';
      const statusUpper = String(status).toUpperCase();
      if (statusUpper === 'APPROVED') return 'success';
      if (statusUpper === 'PENDING' || statusUpper === 'PENDING APPROVAL') return 'warning';
      if (statusUpper === 'REJECTED' || statusUpper === 'REJECT') return 'danger';
      return 'info';
    },
    handleWheel(event) {
      if (document.activeElement === event.target) {
        event.preventDefault()
      }
    },
    onNumberGuard(e) {
      const t = e && e.target ? e.target : null
      if (!t) return
      let val = t.value
      // Allow only digits and one dot
      val = val.replace(/[^0-9.]/g, '')
      const parts = val.split('.')
      if (parts.length > 2) {
        val = parts[0] + '.' + parts.slice(1).join('')
      }
      // Restrict to 2 decimals
      if (parts.length === 2) {
        parts[1] = parts[1].slice(0, 2)
        val = parts[0] + '.' + parts[1]
      }
      t.value = val
      // Sync back to model
      if (t === this.$refs.amountInput) {
        this.item.addTask_amount = val
      }
      if (t === this.$refs.sellingInput) {
        this.item.addTask_selling_amt = val
      }
    },
    onAmountChange() {
      // Clear error on change to allow user to correct
      if (this.item.addTask_amount && Number(this.item.addTask_amount) > 0) {
        this.item.addTask_amount_err = ''
      }
      this.$emit('change', { index: this.index, item: this.item })
    },
    validateAmount() {
      // Validate that amount is greater than zero
      if (this.item.addTask_amount !== '' && this.item.addTask_amount !== null && this.item.addTask_amount !== undefined) {
        const amount = Number(this.item.addTask_amount)
        if (isNaN(amount) || amount <= 0) {
          this.item.addTask_amount_err = 'Amount must be greater than zero'
        } else {
          this.item.addTask_amount_err = ''
        }
      }
      this.$emit('change', { index: this.index, item: this.item })
    },
    onSellingAmountChange() {
      // Clear error on change to allow user to correct
      if (this.item.addTask_selling_amt && Number(this.item.addTask_selling_amt) > 0) {
        this.item.addTask_selling_amt_err = ''
      }
      this.$emit('change', { index: this.index, item: this.item })
    },
    validateSellingAmount() {
      // Validate that selling amount is greater than zero when Bill to Customer is Yes
      if (this.item.addTask_scCategory === 'Yes') {
        if (this.item.addTask_selling_amt !== '' && this.item.addTask_selling_amt !== null && this.item.addTask_selling_amt !== undefined) {
          const sellingAmt = Number(this.item.addTask_selling_amt)
          if (isNaN(sellingAmt) || sellingAmt <= 0) {
            this.item.addTask_selling_amt_err = 'Selling Amount must be greater than zero'
          } else {
            this.item.addTask_selling_amt_err = ''
          }
        }
      }
      this.$emit('change', { index: this.index, item: this.item })
    },
    onSCCategoryChange(val) {
      // Clear selling amount error when category toggles
      this.item.addTask_scCategory_err = ''
      if (val === 'No') {
        this.item.addTask_selling_amt = ''
        this.item.addTask_selling_amt_err = ''
      }
      this.$emit('change', { index: this.index, item: this.item })
    },
  },
}
</script>

<style scoped>
.btn-compact {
  padding: 2px 6px !important;
  font-size: 12px !important;
  line-height: 1.1 !important;
}
</style>

<style scoped>
.error {
  color: red;
}
</style>



// WEBPACK FOOTER //
// AddTaskRow.vue?25f906f6